import {
  useGetMetaIntegration,
  useListWhatsAppAccounts,
} from '@/features/integrations/api';
import { useListLeadForms } from '@/features/lead-forms';
import type { LeadForm } from '@/features/lead-forms/api/types';
import { useListLocations } from '@/features/organization-locations';
import { useResolvedRoutes } from '@/lib/use-routes';
import type { MetaErrorDetail } from '@borradh-workspace/api-client';
import type { MessagingDestination } from '@borradh-workspace/api-client/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { useCreateCampaign } from '../../api';
import type { CampaignPlatformFilterValue } from '../campaign-mobile/campaign-mobile-utils';
import type { OptimizationMode } from './create-campaign-form-fields';
import { buildCreateCampaignPayload } from './create-campaign-form.payload';
import {
  type CreateCampaignFormData,
  createCampaignFormDefaultValues,
  createCampaignFormSchema,
  forcesEngagement,
  showsOptimizationMode,
} from './create-campaign-form.schema';
import {
  defaultCampaignLocationId,
  extractMetaErrorFromResponse,
  getCurrencySymbol,
  toCampaignLocationOptions,
} from './create-campaign-form.utils';

/**
 * The create-campaign core. Owns validation, the Meta-integration derived
 * state (page / currency / destinations availability), the location prefill and
 * — crucially — the mutation payload. The desktop dialog and the mobile funnel
 * are two presentations of this hook; neither may build a payload itself.
 */
export function useCreateCampaignForm(options?: {
  onSuccessNavigate?: boolean;
  onSuccess?: () => void;
}) {
  const navigate = useNavigate();
  const routes = useResolvedRoutes();
  const { executeAsync, isExecuting } = useCreateCampaign(true, false);
  const [metaError, setMetaError] = useState<MetaErrorDetail | null>(null);
  const [showMetaErrorDialog, setShowMetaErrorDialog] = useState(false);

  const {
    integration,
    isConnected,
    isLoading: isLoadingIntegration,
  } = useGetMetaIntegration();

  const pages = useMemo(
    () => (integration?.pages ?? []).filter((p) => p.isActive),
    [integration?.pages]
  );

  const defaultPage = useMemo(
    () =>
      pages.find((p) => p.id === integration?.defaultPageId) ??
      pages[0] ??
      null,
    [pages, integration?.defaultPageId]
  );

  const selectedCurrency = defaultPage?.defaultAdAccountCurrency ?? 'EUR';
  const currencySymbol = useMemo(
    () => getCurrencySymbol(selectedCurrency),
    [selectedCurrency]
  );
  const hasInstagramLinked = !!defaultPage?.linkedInstagramAccountId;

  const { leadForms, isLoading: isLoadingLeadForms } = useListLeadForms({
    limit: 100,
  });
  const syncedLeadForms = useMemo(
    () => leadForms.filter((f) => f.status === 'synced' && !!f.metaFormId),
    [leadForms]
  );
  const hasNoLeadForms = !isLoadingLeadForms && syncedLeadForms.length === 0;

  const { locations, isLoading: isLoadingLocations } = useListLocations();

  const form = useForm<CreateCampaignFormData>({
    // biome-ignore lint/suspicious/noExplicitAny: zodResolver type mismatch with complex schema
    resolver: zodResolver(createCampaignFormSchema) as any,
    defaultValues: createCampaignFormDefaultValues,
  });

  const followUpType = form.watch('followUpType');
  const isChatbot = followUpType === 'chatbot';
  const isLeadForm = followUpType === 'lead_form';
  const destinations = form.watch('destinations') ?? [];
  const optimizationMode = form.watch('optimizationMode');
  const showOptimization = showsOptimizationMode({
    followUpType,
    destinations,
  });
  const leadFormId = form.watch('leadFormId');
  const selectedLeadForm = useMemo(
    () => syncedLeadForms.find((f) => f.id === leadFormId) ?? null,
    [syncedLeadForms, leadFormId]
  );

  const { accounts: whatsappAccounts } = useListWhatsAppAccounts({
    queryConfig: { enabled: isChatbot },
  });
  const hasWhatsApp = whatsappAccounts.length > 0;

  useEffect(() => {
    if (isChatbot && destinations.length === 0) {
      form.setValue('destinations', ['messenger'], { shouldDirty: false });
    } else if (!isChatbot && destinations.length > 0) {
      form.setValue('destinations', [], { shouldDirty: false });
    }
  }, [isChatbot, destinations.length, form]);

  const targetingLocationId = form.watch('targetingLocationId');
  const targetingDistanceKm = form.watch('targetingDistanceKm');

  const locationOptions = useMemo(
    () => toCampaignLocationOptions(locations),
    [locations]
  );
  const selectedLocation =
    locationOptions.find((option) => option.id === targetingLocationId) ?? null;
  const hasLocation = !!selectedLocation;
  const hasNoLocations = !isLoadingLocations && locationOptions.length === 0;
  const hasNoTargetableLocations =
    !isLoadingLocations &&
    locationOptions.length > 0 &&
    !locationOptions.some((option) => option.targetable);

  // Hold the picker on "Loading…" until the prefill below has had its go, so
  // an empty picker doesn't flash before the primary branch lands in it.
  const defaultLocationId = defaultCampaignLocationId(locationOptions);
  const locationPrefillPending =
    !hasLocation && (isLoadingLocations || !!defaultLocationId);

  useEffect(() => {
    if (form.getValues('targetingLocationId')) return;
    if (!defaultLocationId) return;
    form.setValue('targetingLocationId', defaultLocationId, {
      shouldValidate: true,
    });
  }, [defaultLocationId, form]);

  const clearLocation = () => {
    form.setValue('targetingLocationId', '', { shouldValidate: true });
  };

  const setLocationId = (id: string) => {
    const option = locationOptions.find((o) => o.id === id);
    // A branch without a map pin can't be targeted; the pickers render it
    // disabled, and this keeps a stray click from selecting it anyway.
    if (!option?.targetable) return;
    form.setValue('targetingLocationId', id, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  /**
   * Meta blocks Lead Generation optimization for WhatsApp destinations, so any
   * selection that includes WhatsApp forces Engagement.
   */
  const setDestinations = (
    next: MessagingDestination[],
    opts?: { shouldValidate?: boolean }
  ) => {
    form.setValue('destinations', next, {
      shouldValidate: opts?.shouldValidate ?? true,
      shouldDirty: true,
    });
    if (forcesEngagement(next)) {
      form.setValue('optimizationMode', 'engagement');
    }
  };

  const toggleDestination = (value: MessagingDestination) => {
    const next = destinations.includes(value)
      ? destinations.filter((d) => d !== value)
      : [...destinations, value];
    setDestinations(next as MessagingDestination[]);
  };

  const setOptimizationMode = (value: OptimizationMode) => {
    form.setValue('optimizationMode', value, { shouldDirty: true });
  };

  const isDestinationDisabled = (value: MessagingDestination): boolean => {
    if (value === 'whatsapp') return !hasWhatsApp;
    if (value === 'instagram_dm') return !hasInstagramLinked;
    return false;
  };

  const applyPlatformFilter = (platform: CampaignPlatformFilterValue) => {
    if (!isChatbot) return;

    if (platform === 'instagram_dm') {
      if (!hasInstagramLinked) return;
      setDestinations(['instagram_dm'], { shouldValidate: false });
      return;
    }

    if (platform === 'facebook_messenger') {
      setDestinations(['messenger'], { shouldValidate: false });
      return;
    }

    const next: MessagingDestination[] = ['messenger'];
    if (hasInstagramLinked) next.push('instagram_dm');
    if (hasWhatsApp) next.push('whatsapp');
    setDestinations(next, { shouldValidate: false });
  };

  const getPlatformFilterValue = (): CampaignPlatformFilterValue => {
    if (!isChatbot) return 'all';
    if (destinations.length === 1 && destinations[0] === 'instagram_dm') {
      return 'instagram_dm';
    }
    if (destinations.length === 1 && destinations[0] === 'messenger') {
      return 'facebook_messenger';
    }
    return 'all';
  };

  const onSubmit = async (data: CreateCampaignFormData) => {
    if (!defaultPage) {
      toast.error('No active Facebook Page found');
      return;
    }
    if (data.followUpType === 'chatbot' && data.destinations.length === 0) {
      toast.error('Select at least one destination');
      return;
    }
    if (data.followUpType === 'lead_form' && !data.leadFormId) {
      toast.error('Select a lead form');
      return;
    }

    try {
      const result = await executeAsync(
        buildCreateCampaignPayload(data, { metaAdsPageId: defaultPage.id })
      );

      form.reset();
      options?.onSuccess?.();

      if (options?.onSuccessNavigate !== false) {
        void navigate({
          to: routes.advertisingCampaign(result.metaCampaignId),
        });
      }

      return result;
    } catch (error) {
      const metaErrorDetail = await extractMetaErrorFromResponse(error);
      if (metaErrorDetail) {
        setMetaError(metaErrorDetail);
        setShowMetaErrorDialog(true);
      } else {
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        toast.error(message || 'Failed to create campaign');
      }
      // The failure has been fully handled above. React Hook Form does not
      // consume a rejected submit promise, so rethrowing here turns an
      // expected, user-actionable Meta response into a global
      // `unhandledrejection` exception.
      return undefined;
    }
  };

  const isMetaNotConfigured = !isLoadingIntegration && !isConnected;

  return {
    form,
    onSubmit,
    isExecuting,
    isMetaNotConfigured,
    isLoadingIntegration,
    currencySymbol,
    hasInstagramLinked,
    hasWhatsApp,
    hasLocation,
    hasNoLocations,
    hasNoTargetableLocations,
    locationPrefillPending,
    locationOptions,
    selectedLocation,
    targetingDistanceKm,
    clearLocation,
    setLocationId,
    isChatbot,
    isLeadForm,
    followUpType,
    destinations,
    optimizationMode,
    showOptimization,
    setOptimizationMode,
    toggleDestination,
    isDestinationDisabled,
    applyPlatformFilter,
    getPlatformFilterValue,
    syncedLeadForms,
    selectedLeadForm,
    hasNoLeadForms,
    isLoadingLeadForms,
    metaError,
    showMetaErrorDialog,
    setShowMetaErrorDialog,
  };
}

export type { LeadForm };
